import React from "react";
import { Link } from "react-router-dom";

import {
  Phone,
  Mail,
  ArrowUpRight,
  Sparkles,
  Headphones,
  Clock3,
} from "lucide-react";

import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FacebookIcon from "@mui/icons-material/Facebook";
import XIcon from "@mui/icons-material/X";

import contactImg from "../../assets/conatctPageImg.svg";

export default function ContactUsPage() {
  const contactCards = [
    {
      title: "24/7 Live Support",
      subtitle:
        "Talk to our brilliant and knowledgeable support team 24*7*365.",
      value: "+1 (855) 230-0575",
      href: "tel:+18552300575",
      icon: Phone,
      glow: "rgba(212,168,74,0.16)",
      iconBg: "rgba(212,168,74,0.10)",
    },

    {
      title: "Mail Support",
      subtitle:
        "Now is the time to start a chat session with Live Eye Support Reply within 24h",
      value: "support@myliveeye.com",
      href: "mailto:support@myliveeye.com",
      icon: Mail,
      glow: "rgba(212,168,74,0.12)",
      iconBg: "rgba(212,168,74,0.08)",
    },
  ];

  const socials = [
    {
      icon: InstagramIcon,
      href: "https://www.instagram.com/liveeye.inc/",
      label: "Instagram",
    },
    {
      icon: LinkedInIcon,
      href: "https://www.linkedin.com/company/liveeyesurveillance/",
      label: "LinkedIn",
    },
    {
      icon: FacebookIcon,
      href: "https://www.facebook.com/liveeyesurveillance/",
      label: "Facebook",
    },
    {
      icon: XIcon,
      href: "https://twitter.com/myliveeye",
      label: "Twitter",
    },
  ];

  return (
    <div
      className="
        relative
        bg-[rgb(var(--background))]
      "
    >

      <div
        className="
          relative
          z-[2]
          max-w-[1400px]
          mx-auto
          grid
          grid-cols-1
          lg:grid-cols-[minmax(0,1.15fr)_minmax(420px,0.85fr)]
          gap-5
          items-stretch
        "
      >
        {/* =========================================================
            LEFT HERO PANEL
        ========================================================= */}
        <div
          className="
            relative
            card
            transition-all
            duration-500
            hover:shadow-[var(--shadow-card-hover)]
          "
        >
          {/* Decorative Top Gradient */}
          <div
            className="
              absolute
              inset-x-0
              top-0
              h-[1px]
            "
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(212,168,74,0.6), transparent)",
            }}
          />

          <div
            className="
              relative
              z-[2]
              flex
              flex-col
              justify-between
              h-full
              p-5
              sm:p-7
              lg:p-10
            "
          >
            {/* Top */}
            <div>
              {/* Label */}
              <div
                className="
                  inline-flex
                  items-center
                  gap-2
                  rounded-full
                  px-4
                  py-2
                  border
                  border-[rgba(212,168,74,0.14)]
                  bg-[rgba(212,168,74,0.05)]
                  backdrop-blur-sm
                "
              >
                <Sparkles
                  size={13}
                  className="text-[rgb(var(--color-gold-1))]"
                />

                <span className="page-breadcrumb">LIVE EYE ASSISTANCE</span>
              </div>

              {/* Hero Text */}
              <div className="mt-10 max-w-[700px]">
                <h1
                  className="
                    text-[30px]
                    sm:text-[44px]
                    xl:text-[58px]
                    font-extralight
                    leading-[1]
                    text-[rgb(var(--text-primary))]
                  "
                >
                  We’re Always
                  <br />
                  <span
                    className="
                      text-transparent
                      bg-clip-text
                    "
                    style={{
                      backgroundImage:
                        "linear-gradient(135deg, rgb(var(--color-gold-1)) 0%, rgb(var(--color-gold-2)) 100%)",
                    }}
                  >
                    Ready To Help
                  </span>
                </h1>

                <p
                  className="
                    mt-6
                    text-body
                    max-w-[620px]
                    text-[rgb(var(--text-secondary))]
                  "
                >
                  Choose your preferred support channel and connect with our
                  surveillance experts instantly.
                </p>
              </div>
            </div>

            {/* Hero Image */}
            <div
              className="
                relative
                mt-10
                flex
                justify-center
                items-center
              "
            >
              <div
                className="
                  absolute
                  w-[70%]
                  h-[70%]
                  rounded-full
                  blur-[90px]
                  opacity-20
                "
                style={{
                  background:
                    "radial-gradient(circle, rgba(212,168,74,0.25) 0%, transparent 70%)",
                }}
              />

              <img
                src={contactImg}
                alt="Contact Illustration"
                className="
                  relative
                  z-[2]
                  w-full
                  max-w-[320px]
                  object-contain
                  transition-all
                  duration-700
                  hover:scale-[1.02]
                  hover:-translate-y-1
                "
              />
            </div>
          </div>
        </div>

        {/* =========================================================
            RIGHT PANEL
        ========================================================= */}
        <div className="flex flex-col gap-5">
          {/* Contact Cards */}
          <div className="grid gap-5">
            {contactCards.map((item) => (
              <a
                key={item.title}
                href={item.href}
                className="
                  group
                  relative
                  card
                  transition-all
                  overflow-hidden
                  duration-500
                  hover:-translate-y-[3px]
                  hover:shadow-[var(--shadow-card-hover)]
                  hover:border-[rgba(212,168,74,0.16)]
                "
              >
                {/* Glow */}
                <div
                  className="
                    absolute
                    right-[-40px]
                    top-[-40px]
                    h-[140px]
                    w-[140px]
                    rounded-full
                    blur-[70px]
                    opacity-0
                    transition-all
                    duration-500
                    group-hover:opacity-100
                  "
                  style={{
                    background: item.glow,
                  }}
                />

                <div className="relative z-[2]">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div
                      className="
                        h-[48px]
                        w-[48px]
                        rounded-[15px]
                        flex
                        items-center
                        justify-center
                        border
                        border-[rgb(var(--border-primary))]
                      "
                      style={{
                        background: item.iconBg,
                      }}
                    >
                      <item.icon
                        size={20}
                        className="text-[rgb(var(--color-gold-1))]"
                      />
                    </div>

                    <div
                      className="
                        h-[42px]
                        w-[42px]
                        rounded-full
                        flex
                        items-center
                        justify-center
                        bg-[rgb(var(--surface-2))]
                        transition-all
                        duration-300
                        group-hover:translate-x-1
                        group-hover:-translate-y-1
                      "
                    >
                      <ArrowUpRight
                        size={18}
                        className="text-[rgb(var(--text-secondary))]"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="mt-6">
                    <div className="section-title text-[18px] font-medium">
                      {item.title}
                    </div>

                    <p className="text-body-sm mt-3">{item.subtitle}</p>

                    <div
                      className="
                        mt-5
                        text-[15px]
                        font-medium
                        tracking-[-0.01em]
                        text-[rgb(var(--color-gold-1))]
                      "
                    >
                      {item.value}
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Social Card */}
          <div className="card">
            <div className="page-breadcrumb">FOLLOW LIVE EYE</div>

            <div className="flex items-center flex-wrap gap-4 mt-6">
              {socials.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  target="_blank"
                  className="
                    group
                    relative
                    h-[48px]
                    w-[48px]
                    rounded-[15px]
                    border
                    border-[rgb(var(--border-primary))]
                    bg-[rgb(var(--surface-2))]
                    flex
                    items-center
                    justify-center
                    transition-all
                    duration-300
                    hover:-translate-y-[3px]
                    hover:border-[rgba(212,168,74,0.22)]
                    hover:bg-[rgba(212,168,74,0.06)]
                  "
                >
                  <item.icon
                    className="
                      text-[rgb(var(--text-secondary))]
                      transition-all
                      duration-300
                      group-hover:text-[rgb(var(--color-gold-1))]
                    "
                    sx={{
                      fontSize: 22,
                    }}
                  />
                </Link>
              ))}
            </div>

            <p className="text-caption mt-5">
              Stay connected with Live Eye for product updates, support
              highlights, surveillance insights, and latest innovations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
